import { PopupError, PopupSuccess } from "@components/generals/popup";
import { SpinerPage } from "@components/loaders/spiner";
import {
  PermitRequest,
  permitRequestSchema,
} from "@components/resolvers/permit.resolvers";
import { yupResolver } from "@hookform/resolvers/yup";
import { PermitService } from "@services/permit.service";
import { SchoolService } from "@services/school.service";
import { useNotificationStore } from "@stores/notification.store";
import { useThemeStore } from "@stores/theme.store";
import { useMutation, useQuery } from "@tanstack/react-query";
import id from "date-fns/locale/id";
import lodash from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
registerLocale("id", id);
setDefaultLocale("id");

export const PermitFormPage = () => {
  const navigate = useNavigate();
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);
  const { pushNotification } = useNotificationStore((state) => state);

  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [popupError, setPopupError] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [listOpenDate, setListOpenDate] = useState<Date[]>([]);
  const [disabledOption, setDisabledOption] = useState<boolean>(false);
  const [isIOSWebView, setIsIOSWebView] = useState(false);

  const { data: settings } = useQuery({
    queryKey: [PermitService.queries.GET_PERMIT_SETTING],
    queryFn: () => PermitService.setting(),
  });

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data: dataMenginap } = useQuery({
    queryKey: [PermitService.queries.GET_DATA_MENGINAP, id],
    queryFn: () => PermitService.dataMenginap({ id: nis }),
  });

  const { data: dataGender } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_GENDER, nis],
    queryFn: () => SchoolService.studentGender({ nis }),
  });

  const { register, handleSubmit, setValue, control, formState } =
    useForm<PermitRequest>({
      resolver: yupResolver<PermitRequest>(permitRequestSchema),
      shouldFocusError: false,
    });

  const { mutate, isPending } = useMutation({
    mutationFn: PermitService.request,
    onSuccess: (response) => {
      if (response.success) {
        setPopupSuccess(true);
      } else {
        pushNotification({
          type: "error",
          message: response.message,
        });
      }
    },
    onError: (error: any) => {
      pushNotification({
        type: "error",
        message: error.message ?? "Internal Error",
      });
    },
  });

  useEffect(() => {
    dataMenginap?.data.length > 0
      ? setDisabledOption(true)
      : setDisabledOption(false);
  }, [dataMenginap, setDisabledOption]);

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: "Request Izin",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ title: "Request Izin" });
  }, [setFooter, setHeader, setAppBar]);

  useEffect(() => {
    const userAgent = navigator.userAgent;

    // Very specific: only iOS devices in WebView (not Safari browser)
    const isReallyIOSWebView =
      /iPad|iPhone|iPod/.test(userAgent) &&
      !userAgent.includes("Safari/") &&
      !userAgent.includes("Chrome/");

    setIsIOSWebView(isReallyIOSWebView);
  }, []);

  // const onChangeMode = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   const time = e.target.options[e.target.selectedIndex]?.dataset?.time ?? "";
  //   setListOpenDate(
  //     (settings?.data ?? [])
  //       .filter((item) => {
  //         const gender = dataGender?.data?.gender;
  //         const genderMap =
  //           gender?.toLowerCase() === "pria"
  //             ? "PUTRA"
  //             : gender?.toLowerCase() === "wanita"
  //             ? "PUTRI"
  //             : "SEMUA";
  //         return item.gender === genderMap || item.gender === "SEMUA";
  //       })
  //       .filter((item) => item.name === time)
  //       .map((item) => moment(item.date).toDate())
  //   );
  // };

  const onChangeMode = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const time = e.target.options[e.target.selectedIndex]?.dataset?.time ?? "";
    const isMenginap = time === "Menginap" && dataMenginap?.data.length > 0;
    const disabledMonths =
      dataMenginap?.data.length > 0 &&
      dataMenginap?.data.map((item: { tanggalizin: string }) =>
        moment(item.tanggalizin).format("YYYY-MM")
      );

    const dates = (settings?.data ?? [])
      .filter((item) => {
        const gender = dataGender?.data?.gender;
        const genderMap =
          gender?.toLowerCase() === "pria"
            ? "PUTRA"
            : gender?.toLowerCase() === "wanita"
            ? "PUTRI"
            : "SEMUA";
        return item.gender === genderMap || item.gender === "SEMUA";
      })
      .filter((item) => item.name === time)
      .map((item) => {
        const date = moment(item.date).toDate();
        if (
          isMenginap &&
          disabledMonths.includes(moment(date).format("YYYY-MM"))
        ) {
          return null; // disable date if it matches any tanggalizin value
        }
        return date;
      });

    setListOpenDate(dates.filter((date): date is Date => date !== null));
    setStartDate(null);
    setValue("dateBack", "");
    setValue("datePermit", "");
  };

  const onChangeDate = (date: Date | null) => {
    if (date !== null) {
      const formatted = moment(date).format("YYYY-MM-DD HH:mm:ss");
      setValue("dateBack", formatted);
      setValue("datePermit", formatted, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setStartDate(date);
    }
  };

  // For native date input on iOS, also handle the reset
  const onChangeDateNative = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value;
    if (dateValue) {
      const date = new Date(dateValue);
      onChangeDate(date);
    }
  };

  // Generate allowed dates string for native date input
  const getAllowedDatesString = () => {
    if (!listOpenDate || listOpenDate.length === 0) return "";

    return listOpenDate
      .map((date) => moment(date).format("YYYY-MM-DD"))
      .join(",");
  };

  return (
    <>
      <SpinerPage show={isPending} />

      <div className="card card-style">
        <div className="content mb-4 !tw-mt-6">
          <form
            autoComplete="off"
            onSubmit={handleSubmit((data) => mutate(data))}
          >
            <div className="input-style has-borders input-style-always-active no-icon mb-3">
              <label htmlFor="form5" className="color-highlight font-500">
                Pilih Izin
              </label>
              <select
                className="bg-theme"
                {...register("mode")}
                onChange={onChangeMode}
              >
                <option value="">Pilih Izin</option>
                {lodash
                  .uniqBy(
                    (settings?.data ?? []).filter((item) => {
                      const gender = dataGender?.data?.gender;
                      const genderMap =
                        gender?.toLowerCase() === "pria"
                          ? "PUTRA"
                          : gender?.toLowerCase() === "wanita"
                          ? "PUTRI"
                          : "SEMUA";
                      return item.gender === genderMap;
                    }),
                    "name"
                  )
                  .map((item, idx) => (
                    <option
                      key={idx}
                      value={item.name.toUpperCase()}
                      data-time={item.name}
                      // disabled={
                      //   item.type.toLowerCase() === "menginap"
                      //     ? disabledOption
                      //     : false
                      // }
                    >
                      {item.name}{" "}
                      {/* {item.type.toLowerCase() === "menginap"
                        ? `(kuota menginap: ${disabledOption ? 0 : 1})`
                        : ""} */}
                    </option>
                  ))}
              </select>
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4 mt-3">
              <Controller
                name="datePermit"
                control={control}
                render={() => (
                  <>
                    {isIOSWebView ? (
                      // Date select dropdown for mobile (shows only allowed dates)
                      <select
                        value={
                          startDate
                            ? moment(startDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) => {
                          const dateValue = e.target.value;
                          if (dateValue) {
                            const date = new Date(dateValue);
                            onChangeDate(date);
                          }
                        }}
                        className="bg-theme"
                      >
                        <option value="">pilih tanggal</option>
                        {listOpenDate
                          .filter((date) => {
                            const checkDate = moment(date);
                            const today = moment();
                            const maxDate = moment().add(3, "months");
                            return (
                              checkDate.isSameOrAfter(today, "day") &&
                              checkDate.isSameOrBefore(maxDate, "day")
                            );
                          })
                          .map((date, index) => (
                            <option
                              key={index}
                              value={moment(date).format("YYYY-MM-DD")}
                            >
                              {moment(date).format("DD MMMM YYYY")}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <DatePicker
                        selected={startDate}
                        minDate={new Date()}
                        maxDate={moment().add(3, "months").toDate()}
                        onChange={onChangeDate}
                        className="form-control dark:form-control"
                        dateFormat="dd MMMM yyyy"
                        includeDates={listOpenDate}
                        calendarStartDay={0}
                        // onKeyDown={(e) => e.preventDefault()} // block typing
                        customInput={
                          <button
                            type="button"
                            className="form-control dark:form-control tw-text-white tw-text-xs tw-text-left"
                            style={{
                              border: "1px solid #ffffff14",
                              borderRadius: "10px",
                              padding: "18px 13px",
                            }}
                          >
                            {startDate
                              ? moment(startDate).format("DD MMMM YYYY")
                              : "pilih tanggal"}
                          </button>
                        }
                      />
                    )}
                  </>
                )}
              />
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
              <label
                // htmlFor="datePermit"
                className={`font-400 font-13 ${
                  formState.errors.datePermit
                    ? "tw-text-red-500"
                    : "color-highlight"
                }`}
              >
                Tanggal Izin{" "}
                {formState.errors.datePermit && (
                  <>{formState.errors.datePermit.message}</>
                )}
              </label>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              <input
                type="text"
                className="form-control bg-theme"
                placeholder="Nama Penjemput"
                {...register("pickup")}
              />
              <label
                // htmlFor="pickup"
                className={`font-400 font-13 ${
                  formState.errors.pickup
                    ? "tw-text-red-500"
                    : "color-highlight"
                }`}
              >
                Nama Penjemput{" "}
                {formState.errors.pickup && (
                  <>{formState.errors.pickup.message}</>
                )}
              </label>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              {/* <input
                type="text"
                className="form-control bg-theme"
                placeholder="Status Penjemput"
                {...register("pickupStatus")}
              /> */}
              <label
                // htmlFor="pickupStatus"
                className={`font-400 font-13 ${
                  formState.errors.pickupStatus
                    ? "tw-text-red-500"
                    : "color-highlight"
                }`}
              >
                Status Penjemput{" "}
                {formState.errors.pickupStatus && (
                  <>{formState.errors.pickupStatus.message}</>
                )}
              </label>
              <select {...register("pickupStatus")} className="bg-theme">
                <option value="">Pilih Status</option>
                <option value="Orang Tua Kandung">Orang Tua Kandung</option>
                <option value="Paman/Kakek/Bibi">Paman/Kakek/Bibi/Nenek</option>
                <option value="Kakak Laki-Laki/Perempuan Kandung">
                  Kakak Laki-Laki/Perempuan Kandung
                </option>
              </select>
              <span>
                <i>
                  <FaChevronDown />
                </i>
              </span>
            </div>
            <div className="input-style has-borders hnoas-icon input-style-always-active validate-field mb-4">
              <textarea
                className="form-control bg-theme"
                placeholder="catatan ..."
                {...register("notes")}
              />
              <label
                // htmlFor="notes"
                className={`font-400 font-13 ${
                  formState.errors.notes ? "tw-text-red-500" : "color-highlight"
                }`}
              >
                Catatan{" "}
                {formState.errors.notes && (
                  <>{formState.errors.notes.message}</>
                )}
              </label>
            </div>
            <button
              type="submit"
              className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-green-dark tw-grow mt-4 tw-w-full tw-h-11"
            >
              Request Izin
            </button>
          </form>
        </div>
      </div>

      <PopupSuccess
        show={popupSuccess}
        title="Berhasil!"
        message="Berhasil membuat permintaan izin"
        textButton="Kembali"
        onConfirm={() => navigate(-1)}
      />

      <PopupError
        show={popupError}
        title="Gagal!"
        message={formState.errors.datePermit?.message ?? ""}
      />
    </>
  );
};
